import React, { useEffect, useRef, useState } from 'react';
import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import DottedMap from 'dotted-map';
import { stats } from '../data/mock';

const ROTATING_WORDS = ['AI Products', 'Digital Platforms', 'Engineering Teams', 'Scalable Systems'];

const prefersReducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/* ─── City data ─── */
const HUB = { name: 'India', lat: 20.5, lng: 78.9 };

const MARKETS = [
  { name: 'New York', lat: 40.7, lng: -74 },
  { name: 'San Francisco', lat: 37.8, lng: -122.4 },
  { name: 'London', lat: 51.5, lng: -0.1 },
  { name: 'Dubai', lat: 25.2, lng: 55.3 },
  { name: 'Sydney', lat: -33.9, lng: 151.2 },
  { name: 'Toronto', lat: 43.7, lng: -79.4 },
  { name: 'Singapore', lat: 1.3, lng: 103.8 },
  { name: 'Los Angeles', lat: 34.1, lng: -118.2 },
  { name: 'Seattle', lat: 47.6, lng: -122.3 },
  { name: 'Boston', lat: 42.4, lng: -71.1 },
  { name: 'Chicago', lat: 41.9, lng: -87.6 },
  { name: 'Austin', lat: 30.3, lng: -97.7 },
  { name: 'Miami', lat: 25.8, lng: -80.2 },
  { name: 'Denver', lat: 39.7, lng: -105 },
  { name: 'Washington DC', lat: 38.9, lng: -77 },
  { name: 'Vancouver', lat: 49.3, lng: -123.1 },
  { name: 'Montreal', lat: 45.5, lng: -73.6 },
  { name: 'Berlin', lat: 52.5, lng: 13.4 },
  { name: 'Amsterdam', lat: 52.4, lng: 4.9 },
  { name: 'Dublin', lat: 53.3, lng: -6.3 },
  { name: 'Paris', lat: 48.9, lng: 2.3 },
  { name: 'Melbourne', lat: -37.8, lng: 145 },
  { name: 'Tokyo', lat: 35.7, lng: 139.7 },
];

const ALL_CITIES = [HUB, ...MARKETS];

/* ─── Map generation (runs once at module load) ─── */
const _map = new DottedMap({ height: 60, grid: 'diagonal' });
ALL_CITIES.forEach((c) => _map.addPin({ lat: c.lat, lng: c.lng }));

const RAW_MAP_SVG = _map.getSVG({ radius: 0.45, color: '#94a3b8', shape: 'circle' });
const MAP_CIRCLES_HTML = RAW_MAP_SVG
  .replace(/<svg[^>]*>[\s]*/, '')
  .replace(/[\s]*<\/svg>/, '');

const ALL_PINS = _map.pins.map((pin, i) => ({
  x: Math.round(pin.x * 100) / 100,
  y: Math.round(pin.y * 100) / 100,
  ...ALL_CITIES[i],
}));

const INDIA_PIN = ALL_PINS[0];
const CITY_PINS = ALL_PINS.slice(1);

/*
 * Overlay SVG — tiny location pins + subtle AI rings.
 * No animations, no arcs, no particles. Minimal and clean.
 * Half-size teardrop pins with a single static ring.
 */
const OVERLAY_HTML = [
  // ── City location pins (tiny) ──
  ...CITY_PINS.map((pin) => `
    <g transform="translate(${pin.x},${pin.y})">
      <circle cx="0" cy="-0.15" r="1" fill="none" stroke="#2693FF" stroke-width="0.08" opacity="0.2"/>
      <path d="M0,-0.75 C0.42,-0.75 0.7,-0.42 0.7,-0.07 C0.7,0.28 0,0.9 0,0.9 C0,0.9 -0.7,0.28 -0.7,-0.07 C-0.7,-0.42 -0.42,-0.75 0,-0.75 Z" fill="#2693FF" opacity="0.75"/>
      <circle cx="0" cy="-0.15" r="0.2" fill="white"/>
    </g>
  `),

  // ── India hub — slightly bigger pin ──
  `<g transform="translate(${INDIA_PIN.x},${INDIA_PIN.y})">
     <circle cx="0" cy="-0.2" r="1.5" fill="none" stroke="#2693FF" stroke-width="0.1" opacity="0.25"/>
     <circle cx="0" cy="-0.2" r="2.2" fill="none" stroke="#2693FF" stroke-width="0.06" opacity="0.12"/>
     <path d="M0,-1.1 C0.62,-1.1 1.05,-0.62 1.05,-0.1 C1.05,0.42 0,1.35 0,1.35 C0,1.35 -1.05,0.42 -1.05,-0.1 C-1.05,-0.62 -0.62,-1.1 0,-1.1 Z" fill="#2693FF"/>
     <circle cx="0" cy="-0.22" r="0.32" fill="white"/>
   </g>`,
].join('\n');

/* ─── Component ─── */
const HeroSection = () => {
  const statsRef = useRef([]);
  const [currentWord, setCurrentWord] = useState(0);
  const [isWordVisible, setIsWordVisible] = useState(true);

  useEffect(() => {
    if (prefersReducedMotion()) return;
    const interval = setInterval(() => {
      setIsWordVisible(false);
      setTimeout(() => {
        setCurrentWord((prev) => (prev + 1) % ROTATING_WORDS.length);
        setIsWordVisible(true);
      }, 400);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (prefersReducedMotion()) {
      statsRef.current.forEach((stat) => {
        if (stat) stat.classList.add('hero-stat-visible');
      });
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('hero-stat-visible');
        });
      },
      { threshold: 0.1 }
    );
    statsRef.current.forEach((stat) => {
      if (stat) observer.observe(stat);
    });
    return () => observer.disconnect();
  }, []);

  return (
    <section
      className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20"
      style={{ background: '#f8fafc' }}
    >
      {/* ── Top gradient for header readability ── */}
      <div
        className="absolute top-0 left-0 right-0 h-24"
        style={{
          zIndex: 2,
          background: 'linear-gradient(to bottom, #f8fafc 0%, #f8fafc 40%, transparent 100%)',
        }}
      />

      {/* ── World Map Background — more visible on mobile/tablet, subtle on desktop ── */}
      <div
        className="absolute inset-0 pointer-events-none opacity-100 lg:opacity-[0.55]"
        style={{ zIndex: 1 }}
        aria-hidden="true"
      >
        <svg
          viewBox="-2 -1 123 62"
          width="100%"
          height="100%"
          preserveAspectRatio="xMidYMid meet"
          xmlns="http://www.w3.org/2000/svg"
        >
          {/* Map dots */}
          {/* eslint-disable-next-line react/no-danger */}
          <g opacity={0.8} dangerouslySetInnerHTML={{ __html: MAP_CIRCLES_HTML }} />

          {/* City pins + India hub */}
          {/* eslint-disable-next-line react/no-danger */}
          <g dangerouslySetInnerHTML={{ __html: OVERLAY_HTML }} />
        </svg>
      </div>

      {/* ── Content ── */}
      <div
        className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28 text-center"
        style={{ zIndex: 10 }}
      >
        <div
          className="absolute inset-0 -mx-8 rounded-3xl"
          style={{
            background: 'radial-gradient(ellipse at center, rgba(248,250,252,0.92) 0%, rgba(248,250,252,0.75) 50%, transparent 75%)',
            zIndex: -1,
          }}
        />

        <div className="hero-fade-in inline-flex items-center gap-2.5 px-4 py-2 rounded-full border border-slate-200 bg-white/90 backdrop-blur-sm mb-8 shadow-sm">
          <span className="w-2 h-2 rounded-full animate-pulse" style={{ background: '#2693FF' }} />
          <span className="text-sm text-slate-600 font-medium">
            Your Engineering Partner for Complex Challenges
          </span>
        </div>

        <h1 className="hero-fade-in font-heading text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-slate-900 mb-6 sm:mb-8 leading-[1.08] tracking-tight">
          We build{' '}
          <span className="relative inline-block">
            <span
              className={`inline-block transition-all duration-500 ${
                isWordVisible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-3'
              }`}
              style={{ color: '#2693FF' }}
            >
              {ROTATING_WORDS[currentWord]}
            </span>
          </span>
        </h1>

        <p className="hero-fade-in-delay-1 text-lg sm:text-xl text-slate-600 max-w-2xl mx-auto mb-10 leading-relaxed">
          Our cross-functional teams have delivered 100+ products for companies ranging from enterprises to Series A+ startups.
        </p>

        <div className="hero-fade-in-delay-2 flex flex-col sm:flex-row items-center justify-center gap-4 mb-16 sm:mb-20">
          <a
            href="https://calendar.app.google/MfHsR1LurH5q2Rii7"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-gradient group inline-flex items-center gap-2.5 px-8 py-4 rounded-full text-white font-semibold text-base cursor-pointer"
            style={{ boxShadow: '0 8px 30px rgba(38, 147, 255, 0.3)' }}
          >
            Schedule a call
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </a>
          <Link
            to="/case-studies"
            className="group inline-flex items-center gap-2.5 px-8 py-4 rounded-full font-medium text-slate-700 border border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50 transition-all duration-300"
          >
            View our work
            <ArrowRight className="w-4 h-4 text-slate-400 group-hover:translate-x-1 group-hover:text-[#2693FF] transition-all" />
          </Link>
        </div>

        <div className="hero-fade-in-delay-3 grid grid-cols-2 md:grid-cols-4 gap-8 sm:gap-12 max-w-4xl mx-auto pt-10 border-t border-slate-200/60">
          {stats.map((stat, index) => (
            <div
              key={index}
              ref={(el) => (statsRef.current[index] = el)}
              className="hero-stat opacity-0 text-center"
              style={{ transitionDelay: `${index * 150}ms` }}
            >
              <div className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold text-slate-900 mb-1.5">
                {stat.value}
                <span style={{ color: '#2693FF' }}>{stat.suffix}</span>
              </div>
              <div className="text-xs sm:text-sm text-slate-500 tracking-wider uppercase font-medium">
                {stat.label}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div
        className="absolute bottom-0 left-0 right-0 h-32"
        style={{ zIndex: 5, background: 'linear-gradient(to top, #f8fafc, transparent)' }}
      />
    </section>
  );
};

export default HeroSection;
